import { appInject } from "@core/di/utils";
import { DI_TOKENS } from "@shared/constants/di";
import { IUploadService } from "@shared/interfaces/upload-service.interface";
import { TierDto } from "@shared/types/tiers/tiers.dto";
import {
  Alert,
  Button,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
} from "antd";
import { useEffect, useState } from "react";

const requiredFieldRule = {
  required: true,
  whitespace: false, // inputNumber doesn't support whitespace check properly usually, handled by type
  message: "This field is required",
};

interface IModal {
  open: boolean;
  tier: TierDto | null;
  onClose: () => void;
  onSave: (data: TierDto) => void;
  isLoading: boolean;
  isError?: boolean;
}

const ChangeTierModal = ({
  open,
  tier,
  onClose,
  onSave,
  isLoading,
  isError,
}: IModal) => {
  const isEditingMode = !!tier?.id;
  const [form] = Form.useForm();

  // Upload State
  const [imageUploaded, setImageUploaded] = useState(false);
  const [imageUploading, setImageUploading] = useState(false);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);

  const uploadService = appInject<IUploadService>(DI_TOKENS.uploadService);

  useEffect(() => {
    if (tier) {
      form.setFieldsValue({
        ...tier,
        icon: tier.icon ?? undefined,
      });
    } else {
      form.resetFields();
      form.setFieldsValue({ isBonusApplicable: true, showGlobalRank: false });
      setImagePreview(null);
      setImageFile(null);
      setImageUploaded(false);
    }
  }, [form, tier, open]);

  const handleFinish = (values: any) => {
    const payload: TierDto = {
      ...values,
      icon: values.icon ?? null,
    };
    onSave(payload);
  };

  return (
    <Modal
      title={isEditingMode ? "Edit Tier" : "Add Tier"}
      open={open}
      onCancel={onClose}
      footer={null}
    >
      <Form
        form={form}
        requiredMark={false}
        layout="vertical"
        onFinish={handleFinish}
      >
        {isError && (
          <Form.Item>
            <Alert
              message="Error"
              description="An error occurred while saving the tier data. Please try again."
              type="error"
              showIcon
            />
          </Form.Item>
        )}

        <Form.Item name="name" label="Name" rules={[requiredFieldRule]}>
          <Input placeholder="e.g. Hall of Fame 🏆" />
        </Form.Item>

        {/* Image Upload Section */}
        <Form.Item label="Icon Image">
          <input
            type="file"
            accept="image/*"
            onChange={(e) => {
              const file = e.target.files?.[0] || null;
              setImageUploaded(false);
              setImageFile(file);
              setImagePreview(file ? URL.createObjectURL(file) : null);
            }}
          />
          {imagePreview && (
            <div style={{ marginTop: 10 }}>
              <img
                src={imagePreview}
                alt="Selected"
                style={{ width: "100%", maxHeight: 200, objectFit: "contain" }}
              />

              {imageUploaded === false ? (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "flex-end",
                  }}
                >
                  <Button
                    type="primary"
                    disabled={imageUploading}
                    onClick={async () => {
                      if (!imageFile) return;
                      try {
                        setImageUploading(true);
                        const {
                          data: { url },
                        } = await uploadService.uploadImage(imageFile);
                        if (url) {
                          setImageUploaded(true);
                        }
                        form.setFieldsValue({ icon: url });
                      } catch (err) {
                        console.error("Failed to upload image", err);
                      } finally {
                        setImageUploading(false);
                      }
                    }}
                    style={{ marginTop: 10 }}
                  >
                    {imageUploading ? "Uploading..." : "Use Image"}
                  </Button>
                </div>
              ) : null}
            </div>
          )}
        </Form.Item>

        <Form.Item name="icon" label="Icon URL (Optional)">
          <Input type="url" placeholder="https://..." />
        </Form.Item>

        <div
          style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}
        >
          <Form.Item
            name="rank"
            label="Tier Entry Threshold (GLO)"
            rules={[{ required: true, message: "Required" }]}
          >
            <InputNumber style={{ width: "100%" }} placeholder="e.g. 1" />
          </Form.Item>

          <Form.Item
            name="gloThreshold"
            label="Tier Base GLO value"
            rules={[{ required: true, message: "Required" }]}
          >
            <InputNumber style={{ width: "100%" }} placeholder="e.g. 10000" />
          </Form.Item>
        </div>

        <Form.Item
          name="percentile"
          label="Percentile (Visual)"
          rules={[{ required: true, message: "Required" }]}
        >
          <Input placeholder="e.g. Top 0.1%" />
        </Form.Item>

        <Form.Item name="isBonusApplicable" label="Is eligible for bonuses?">
          <Select placeholder="Select if eligible">
            <Select.Option value={true}>Yes</Select.Option>
            <Select.Option value={false}>No</Select.Option>
          </Select>
        </Form.Item>

        <Form.Item
          name="showGlobalRank"
          label="Show global rank (users in this tier see rank/percentile)"
        >
          <Select placeholder="Select">
            <Select.Option value={true}>Yes</Select.Option>
            <Select.Option value={false}>No</Select.Option>
          </Select>
        </Form.Item>

        <Space
          style={{ justifyContent: "flex-end", width: "100%", marginTop: 15 }}
        >
          <Button onClick={onClose}>Cancel</Button>
          <Button type="primary" htmlType="submit" loading={isLoading}>
            {isEditingMode ? "Save" : "Add"}
          </Button>
        </Space>
      </Form>
    </Modal>
  );
};

export default ChangeTierModal;
